import { useCallback, useEffect, useRef, useState } from "react";
import { Mail, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { getMyMailbox, authorizeMyMailbox } from "../../lib/api";

// "Connect my mailbox": lets any signed-in user (key account managers too)
// connect their own Gmail so BR OS can send quotes and emails as them and read
// their inbox. The backend only accepts the user's own address.
export default function MyMailboxButton() {
  const [status, setStatus] = useState(null);
  // Only react to popups this button opened (the Integrations page opens its own).
  const pending = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await getMyMailbox();
      setStatus(res.data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || !pending.current) return;
      pending.current = false;
      if (event.data?.type === "google_auth_success") {
        toast.success(`Mailbox connected: ${event.data.email}`);
        load();
      } else if (event.data?.type === "google_auth_error") {
        toast.error(`Mailbox not connected: ${event.data.error}`);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [load]);

  if (!status) return null;

  if (status.connected && !status.needs_calendar) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-muted-foreground w-full mb-2"
        title={`${status.email} is connected`}
        data-testid="my-mailbox-connected"
      >
        <MailCheck className="w-4 h-4" />
        <span>Mailbox connected</span>
      </div>
    );
  }

  const connect = async () => {
    try {
      const res = await authorizeMyMailbox();
      pending.current = true;
      const width = 600;
      const height = 700;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      window.open(res.data.authorization_url, "Connect mailbox", `width=${width},height=${height},left=${left},top=${top}`);
    } catch {
      toast.error("Could not start the mailbox connection");
    }
  };

  // Google revoked the old connection: say so, so nobody reads the old
  // "connected" state as still working.
  const reconnect = Boolean(status.needs_reconnect);
  const addCalendar = Boolean(status.connected && status.needs_calendar);

  return (
    <button
      onClick={connect}
      className={`flex items-center gap-2 text-sm transition-colors w-full mb-2 ${
        reconnect || addCalendar ? "text-amber-700 hover:text-amber-900" : "text-primary hover:text-foreground"
      }`}
      title={
        addCalendar
          ? `Reconnect ${status.mailbox || status.email} with calendar access so the assistant can move and edit your meetings`
          : reconnect
          ? `Google stopped accepting the connection for ${status.email}. Reconnect so BR OS can read and send your email again.`
          : `Connect ${status.email} so BR OS can send and read email as you`
      }
      data-testid={addCalendar ? "add-calendar-access" : reconnect ? "reconnect-my-mailbox" : "connect-my-mailbox"}
    >
      <Mail className="w-4 h-4" />
      <span>{addCalendar ? "Add calendar access" : reconnect ? "Mailbox disconnected: reconnect" : "Connect my mailbox"}</span>
    </button>
  );
}
